/**
 * Search utilities for listings (Story 3.8)
 */

export interface SearchFilters {
  city?: string;
  state?: string;
  zip?: string;
}

/**
 * US state names to abbreviations mapping
 */
const STATE_NAME_TO_ABBR: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'district of columbia': 'DC',
};

/**
 * Set of valid state abbreviations for quick lookup
 */
const VALID_STATE_ABBRS = new Set(Object.values(STATE_NAME_TO_ABBR));

/**
 * Check if input is a valid 2-letter state code
 */
export const isValidStateCode = (input: string): boolean => {
  if (input.length !== 2) return false;
  return VALID_STATE_ABBRS.has(input.toUpperCase());
};

/**
 * Parse search input to determine if it's a city, state, or zip
 * - 5-digit number = zip code
 * - 2-letter uppercase that is a valid state abbreviation = state
 * - Full state name (e.g., "California", "New York") = state
 * - Otherwise = city name
 */
export const parseSearchInput = (input: string): SearchFilters => {
  const trimmed = input.trim();

  if (!trimmed) {
    return {};
  }

  // 5-digit number = zip code
  if (/^\d{5}$/.test(trimmed)) {
    return { zip: trimmed };
  }

  // 2-letter input - check if it's a valid state abbreviation
  if (/^[A-Za-z]{2}$/.test(trimmed)) {
    const upperAbbr = trimmed.toUpperCase();
    if (VALID_STATE_ABBRS.has(upperAbbr)) {
      return { state: upperAbbr };
    }
    // Not a valid state abbreviation, treat as city
    return { city: trimmed };
  }

  // Check if input matches a full state name
  const lowerTrimmed = trimmed.toLowerCase();
  const stateAbbr = STATE_NAME_TO_ABBR[lowerTrimmed];
  if (stateAbbr) {
    return { state: stateAbbr };
  }

  // Otherwise = city name
  return { city: trimmed };
};
